import { ClipboardCheck } from 'lucide-react';
import { Card, CardHeader } from '../_primitives';
import {
  getHumanVerifyUnverifiedCount,
  type HumanVerifyItem,
  type HumanVerifyState,
} from '@/lib/humanVerify';
import {
  HumanVerifyItemRow,
  type HumanVerifyResolution,
} from '@/components/human-verify/HumanVerifyItemRow';

export interface ReviewerSignoffCardProps {
  humanVerify: HumanVerifyState | null;
  resolvingItemId?: string | null;
  onResolve: (item: HumanVerifyItem, status: HumanVerifyResolution, note: string) => void;
  onOpenModal: (itemId?: string) => void;
}

export function ReviewerSignoffCard({
  humanVerify,
  resolvingItemId,
  onResolve,
  onOpenModal,
}: ReviewerSignoffCardProps) {
  const items = humanVerify?.items ?? [];
  const unverifiedCount = getHumanVerifyUnverifiedCount(humanVerify);

  return (
    <Card railSection="reviewer-signoff">
      <CardHeader
        icon={<ClipboardCheck className="size-3.5" />}
        title="Reviewer sign-off"
        trailing={
          <span
            className={
              unverifiedCount > 0
                ? 'rounded-md border border-coral/30 bg-coral-light px-1.5 py-0.5 text-[10.5px] font-semibold text-coral-dark'
                : 'rounded-md border border-teal/30 bg-teal-light px-1.5 py-0.5 text-[10.5px] font-semibold text-teal-dark'
            }
          >
            {unverifiedCount} unverified
          </span>
        }
      />

      <div className="px-3 pb-3">
        {items.length > 0 ? (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onOpenModal()}
              className="inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md bg-coral px-2.5 text-[12px] font-semibold text-primary-foreground hover:bg-coral-hover"
            >
              <ClipboardCheck className="size-3.5" />
              Review flagged items
            </button>
            <div className="max-h-[520px] space-y-2 overflow-y-auto pr-0.5">
              {items.map((item) => (
                <HumanVerifyItemRow
                  key={item.id}
                  item={item}
                  onResolve={onResolve}
                  resolving={resolvingItemId === item.id}
                  compact
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border bg-warm-bg px-3 py-4 text-center text-[12px] text-muted-foreground">
            No flagged reviewer checks for this article.
          </div>
        )}
      </div>
    </Card>
  );
}
